"use client";

import React, { useState, useMemo, useEffect } from 'react';
import Image from 'next/image';
import type { Dog } from '@/lib/types';
import { useAnalytics } from '@/hooks/use-analytics';
import { getPlaceholderImage, cn } from '@/lib/utils';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ArrowUpRight, Mars, Venus } from 'lucide-react';

interface DogCardProps {
  dog: Dog;
  compact?: boolean;
  priority?: boolean;
  onViewDetails?: (dog: Dog) => void;
}

export function DogCard({ dog, compact = false, priority = false, onViewDetails }: DogCardProps) {
  const { logEvent } = useAnalytics();

  // Debugging: Verify deployment
  useEffect(() => {
    // console.log(`[DogCard] Rendering card for: ${dog.name}`);
  }, [dog.name]);

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    logEvent(dog.id, 'view_details_click');
    if (onViewDetails) {
      onViewDetails(dog);
    }
  };

  // Simple age calculation
  const age = useMemo(() => {
    if (!dog.dateOfBirth) return null;
    const dob = new Date(dog.dateOfBirth);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - dob.getTime());
    const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
    if (diffWeeks < 1) return 'Newborn';
    if (diffWeeks < 20) return `${diffWeeks} weeks`;
    const diffMonths = Math.floor(diffWeeks / 4.345);
    return `${diffMonths} months`;
  }, [dog.dateOfBirth]);

  const statusMap: Record<string, { color: string; dot: string; label: string }> = {
    'Available': { color: 'bg-emerald-50 text-emerald-700 border-emerald-100', dot: 'bg-emerald-500', label: 'Available' },
    'Reserved': { color: 'bg-amber-50 text-amber-700 border-amber-100', dot: 'bg-amber-500', label: 'Reserved' },
    'Sold': { color: 'bg-muted text-muted-foreground border-border', dot: 'bg-muted-foreground', label: 'Found Home' },
    'Pending': { color: 'bg-blue-50 text-blue-700 border-blue-100', dot: 'bg-blue-500', label: 'Pending' },
  };

  const statusConfig = statusMap[dog.status] || { color: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground', label: dog.status };

  return (
    <>
      <Link
        href={`/dogs/${dog.id}`}
        onClick={handleCardClick}
        className={cn(
          "group relative block w-full bg-card transition-all duration-500 ease-out",
          compact ? "aspect-square rounded-3xl" : "aspect-[3/4] rounded-3xl",
          "hover:shadow-soft-hover hover:scale-[1.01] shadow-soft border border-border/50 overflow-hidden"
        )}
      >
        {/* Image Container */}
        <div className="absolute inset-0 z-0">
          <Image
            src={dog.primaryImage || dog.images[0] || getPlaceholderImage(`${dog.breed} puppy`)}
            alt={`Purebred ${dog.breed} - ${dog.name}`}
            fill
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority={priority}
            quality={65}
          />
          {/* Overlay gradient - Softened */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />
        </div>

        {/* Status Badge */}
        {!compact && (
          <div className={cn(
            "absolute top-4 left-4 z-20 px-4 py-2 rounded-full border flex items-center gap-2 backdrop-blur-md transition-all duration-300 group-hover:scale-105 shadow-sm",
            statusConfig.color
          )}>
            <div className={`w-2 h-2 rounded-full ${statusConfig.dot} ${dog.status === 'Available' ? 'animate-pulse' : ''}`} />
            <span className="text-xs font-bold uppercase tracking-wider">{statusConfig.label}</span>
          </div>
        )}

        {/* Content Card */}
        {!compact && (
          <div className="absolute bottom-0 left-0 right-0 z-20 p-5">
            <div className="bg-card/95 backdrop-blur-xl rounded-2xl p-5 shadow-soft border border-border/50 transform transition-all duration-500 group-hover:translate-y-[-4px]">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <p className="text-[10px] font-bold tracking-[0.15em] text-muted-foreground uppercase mb-2">{dog.breed}</p>
                  <h3 className="font-headline text-2xl md:text-3xl text-foreground leading-none tracking-tight mb-1">{dog.name}</h3>
                </div>
                <div className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500 shrink-0">
                  <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
                </div>
              </div>

              <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium pt-3 border-t border-border/50">
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/50">
                  {dog.gender === 'Male' ? <Mars className="w-3.5 h-3.5 text-blue-400" /> : <Venus className="w-3.5 h-3.5 text-pink-400" />}
                  <span className="font-semibold">{dog.gender}</span>
                </span>
                {/* {age && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/50">
                    <span className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                    <span className="font-semibold">{age}</span>
                  </span>
                )} */}
                {(dog.price !== undefined && dog.price !== null) ? (
                  <span className="ml-auto font-headline font-bold text-foreground text-xl">${dog.price?.toLocaleString()}</span>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </Link>
    </>
  );
}